import { useState, useEffect, useMemo, useRef } from 'react'
import { isCancel } from 'axios'
import { getApiClient } from '../api'
import type {
  DataResponse,
  FiltersResponse,
  GetDataByScopeParams,
} from '../api'
import { repeatParamsSerializer } from '../utils/axiosHelper'
import type { ActiveFilters } from '../types/Stats'
import { useLocalizationLabels } from '../context/LocalizationLabelContext'
import { isInsights } from '../utils/insights'

type DataParams = GetDataByScopeParams & Record<string, unknown>

export function useStatsChartData(
  scope:               string,
  geo:                 string,
  metric:              string | null,
  appliedFilters:      ActiveFilters,
  filtersResp:         FiltersResponse | null,
  onCompareGeoLoaded:  (list: string[]) => void,
) {
  const { geoNameOptionLabels, statsLabels } = useLocalizationLabels()
  const [chartData, setChartData] = useState<DataResponse | null>(null)
  const [loading,   setLoading]   = useState(false)
  const [dataError, setDataError] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const activeSource = appliedFilters.source
  const sourceUrl    = filtersResp?.details[activeSource]?.url         ?? null
  const chartTypes   = filtersResp?.details[activeSource]?.chart_types ?? ['bar']
  const sourceLabel  = statsLabels.source?.[activeSource]
  const sourceName   = sourceLabel?.name         ?? activeSource
  const licenseName  = sourceLabel?.license_name ?? null
  const licenseUrl   = sourceLabel?.license_url  ?? null

  const fetchKey = useMemo(() => {
    if (!metric || !appliedFilters.source) return null
    return JSON.stringify({ scope, geo, metric, ...appliedFilters })
  }, [scope, geo, metric, appliedFilters])

  useEffect(() => {
    if (!fetchKey || !metric) return
    const { yearRange, compareGeo, unit, source, ...dimFilters } = appliedFilters
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setDataError(false)
    const params: DataParams = {
      geo,
      metric,
      source,
      unit,
      year_from: yearRange[0],
      year_to:   yearRange[1],
      ...(compareGeo.length > 0 ? { compare_geo: compareGeo } : {}),
      ...dimFilters,
    }

    getApiClient()
      .getDataByScope(scope, params, {
        paramsSerializer: repeatParamsSerializer,
        signal:           controller.signal,
      })
      .then((data: DataResponse) => {
        setChartData(data)
        if (data.compare_geo) onCompareGeoLoaded(data.compare_geo)
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return
        console.error('Failed to load chart data', err)
        setChartData(null)
        setDataError(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [fetchKey])

  const orderedData = useMemo(() => {
    if (!chartData || !geo) return null
    const out: Record<string, Record<string, number>> = {}
    for (const key of [geo, ...appliedFilters.compareGeo]) {
      if (chartData.data[key] !== undefined) out[key] = chartData.data[key]
    }
    return out
  }, [chartData, geo, appliedFilters.compareGeo])

  const missingCompareGeo = useMemo(() => {
    if (!orderedData) return []
    const returned = new Set(Object.keys(orderedData))
    return appliedFilters.compareGeo
      .filter(g => !returned.has(g))
      .map(g => geoNameOptionLabels[g]?.label ?? g)
  }, [orderedData, appliedFilters.compareGeo, geoNameOptionLabels])

  const isEmpty      = orderedData !== null && Object.keys(orderedData).length === 0
  const hasChart     = !dataError && !isEmpty && orderedData !== null
  const isRefreshing = loading && hasChart

  return {
    orderedData,         
    loading,
    dataError,
    isEmpty,
    hasChart,
    isRefreshing,
    missingCompareGeo,
    sourceUrl,
    sourceName,
    licenseName,
    licenseUrl,
    chartTypes,
    insights: isInsights(chartData?.insights) ? chartData!.insights : null,
  }
}